import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PackageMinus, Trash2, Upload } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

type Prod = { productId: string; sku: string; name: string; saldo: number }
type Linha = { sku: string; quantity: number }

/** Extrai SKU + Unidades da "GUIA DE SEPARAÇÃO TOTAL".
 *  O PDF sai com colunas grudadas → isolamos a região (após "Conferido" até "TOTAL")
 *  e quebramos cada linha pela caixinha "[ ]" (1º nº = SKU, último = Unidades). */
function parseGuia(text: string): Linha[] {
  let region = text
  const hdr = /Conferido|Unidades/i.exec(region)
  if (hdr) region = region.slice(hdr.index + hdr[0].length)
  const end = region.search(/TOTAL/i) // "TOTAL107" vem grudado — sem \b
  if (end >= 0) region = region.slice(0, end)
  const out: Linha[] = []
  for (const seg of region.split(/\[\s*\]/)) {
    const nums = seg.match(/\d+/g)
    if (!nums || nums.length < 2) continue
    const sku = nums[0]
    const quantity = Number(nums[nums.length - 1])
    if (quantity > 0 && Number(sku) < 100000) out.push({ sku, quantity })
  }
  return out
}

export function BaixaSeparacaoPage() {
  const tenantId = useActiveTenant()
  const [reference, setReference] = useState('')
  const [items, setItems] = useState<Linha[]>([])
  const [busy, setBusy] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const { data: prods = [] } = useQuery<Prod[]>({
    queryKey: ['count-sheet', tenantId],
    queryFn: async () => (await api.get('/stock/count-sheet', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const bySku = useMemo(() => new Map(prods.map(p => [p.sku.trim(), p])), [prods])

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  function carregar(text: string, ref?: string) {
    setItems(parseGuia(text))
    if (ref) setReference(ref)
    else { const m = /SEP-\d{8}-\d+/i.exec(text); if (m && !reference) setReference(m[0]) }
  }

  async function uploadRelatorio(file: File) {
    setExtracting(true); setMsg(null)
    try {
      const isJson = file.name.toLowerCase().endsWith('.json') || file.type === 'application/json'
      if (isJson) {
        // Formato limpo gerado pelo Python — sem ambiguidade.
        const data = JSON.parse(await file.text())
        const its: Linha[] = (data.itens ?? [])
          .map((i: any) => ({ sku: String(i.sku).trim(), quantity: Number(i.quantidade ?? i.quantity) }))
          .filter((i: Linha) => i.quantity > 0)
        setItems(its)
        if (data.separacao) setReference(String(data.separacao))
      } else {
        const b64 = await new Promise<string>((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file)
        })
        const { data } = await api.post('/stock/extract-report', { fileBase64: b64 })
        carregar(data.text ?? '')
      }
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao ler o arquivo')
    } finally { setExtracting(false) }
  }

  const validos = items.filter(l => bySku.has(l.sku.trim()))
  const totalUnidades = validos.reduce((s, l) => s + (l.quantity || 0), 0)

  async function darBaixa() {
    if (!validos.length || busy) return
    if (!window.confirm(`Dar baixa de ${totalUnidades} unidade(s) em ${validos.length} SKU(s)?`)) return
    setBusy(true); setMsg(null)
    try {
      const { data: r } = await api.post('/stock/outbound-batch', { tenantId, items: validos, reference })
      setMsg(`Baixa aplicada: ${r.baixados} SKU(s), ${r.unidades} unidade(s).`)
      setItems([]); setReference('')
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao dar baixa')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Baixa por separação</h2>
        <p className="text-sm text-gray-500">Suba o relatório de separação (PDF) — o WsFull lê a guia e baixa o estoque do que saiu. Confira e ajuste antes de aplicar.</p>
      </div>

      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{msg}</div>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="py-4 space-y-3">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-3 py-6 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600">
              <Upload className="h-4 w-4" />
              {extracting ? 'Lendo…' : 'Enviar relatório (JSON recomendado, ou PDF)'}
              <input type="file" accept="application/json,.json,application/pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadRelatorio(f); e.currentTarget.value = '' }} />
            </label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Referência (ex: SEP-20260710-01)"
              value={reference}
              onChange={e => setReference(e.target.value)}
            />
            <textarea
              className="h-40 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs"
              placeholder={"Ou cole a GUIA DE SEPARAÇÃO aqui...\n9  Serum 360 Vitamina C - 30ml  65\n7  Serum Repair Advanced - 30ml  21"}
              onChange={e => carregar(e.target.value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">Prévia ({validos.length} SKU · {totalUnidades} un)</span>
              <Button size="sm" loading={busy} disabled={validos.length === 0} onClick={darBaixa}>
                <PackageMinus className="h-4 w-4" /> Dar baixa
              </Button>
            </div>
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Suba o relatório (ou cole a guia) para ver a prévia.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-600">Unid.</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((l, i) => {
                    const p = bySku.get(l.sku.trim())
                    return (
                      <tr key={i} className={p ? '' : 'bg-red-50'}>
                        <td className="px-4 py-2 font-mono font-medium">{l.sku}</td>
                        <td className="px-4 py-2 text-gray-600">{p ? p.name : <Badge variant="danger">SKU não encontrado</Badge>}</td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number" min={0}
                            className="w-20 rounded border border-gray-300 px-2 py-1 text-right focus:border-blue-500 focus:outline-none"
                            value={l.quantity}
                            onChange={e => setItems(arr => arr.map((x, j) => j === i ? { ...x, quantity: Number(e.target.value) } : x))}
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button onClick={() => setItems(arr => arr.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
